import { PolicyValueType } from 'components/PolicyFilterDropdown';
import { atom } from 'jotai';
import { atomWithReset, atomWithStorage } from 'jotai/utils';
import callApi from 'lib/callApi';
import moment from 'moment';
import { companyIdAtom } from 'views/state';

import { GroupingDataItem } from './types';
import {
  applyIntervalFilter,
  applyTimespanFilter,
  mergeArrays,
} from '../helper';
import {
  IntervalFilter,
  IntervalFilterType,
  Severity,
  TimespanFilter,
} from '../types';

// DASHBOARD FILTERS
export const criticalityFiltersAtom = atomWithStorage<string[]>(
  'pc_dashboard_criticality_filter',
  [
    Severity[Severity.Urgent],
    Severity[Severity.Critical],
    Severity[Severity.Serious],
    Severity[Severity.Medium],
    Severity[Severity.Minimal],
  ],
);
export const showHistoryChartCountsAtom = atomWithStorage(
  'pc_dashboard_show_counts',
  false,
);

export const timeSpanAtom = atomWithReset(TimespanFilter.SixMonth);
export const intervalAtom = atomWithReset(IntervalFilter.Week);
export const pcRefreshAtom = atomWithStorage<string>('pc_refresh', '');
export const pcCurrentVulnCountAtom = atomWithStorage<string>(
  'pcCurrentVulnCount',
  '',
);

// policy filter atom, value should either 'all' or and integer value (id of the policy)
// define a type

export const policyIdFilterAtom = atomWithStorage<PolicyValueType>(
  'pc_dashboard_policy_filter',
  'all',
);

export const historyChartsOpenAtom = atom(true);
export const currentVulnChartsOpenAtom = atom(true);

// selected PC items for individual dashboards
export const appliedHostIdsAtom = atomWithReset<any[]>([]);
export const isGroupingByQidVulnListingOpenAtom = atom(false);

// Fetch dashboard data from the API
export const dashboardDataAtom = atom(async (get) => {
  const companyId = get(companyIdAtom);
  const appliedHostIds = get(appliedHostIdsAtom);
  const _refresh = get(pcRefreshAtom);

  try {
    if (companyId && companyId !== -1) {
      const userTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
      const params = {
        company_id: companyId,
        // host_type: DashboardType.PC,
        timezone: userTimezone,
      };
      if (appliedHostIds?.length > 0) {
        params['obj_id_list'] = appliedHostIds;
      }

      const responseData = await callApi('pcm/dashboard/', 'GET', params);
      return responseData?.data;
    }
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error(error);
  }
  return undefined;
});

// Get host counts from the dashboard data
export const hostCountsAtom = atom((get) => {
  const data = get(dashboardDataAtom);
  return data?.hosts_counts || 0;
});

// Get labels for the history chart
export const historyChartLabelsAtom = atom((get) => {
  const data = get(dashboardDataAtom);

  const timespanFilter = get(timeSpanAtom);
  const intervalFilter = get(intervalAtom);
  return (
    (data?.historical_data &&
      applyIntervalFilter(
        applyTimespanFilter(data.historical_data?.weeks, timespanFilter),
        intervalFilter,
        IntervalFilterType.SkipRecords,
      )) ||
    []
  );
});

/**
 * Current Charts Data Atom
 */
export const posturesByCriticalityChartDataAtom = atom((get) => {
  const data = get(dashboardDataAtom);
  const policy = get(policyIdFilterAtom);
  const criticalityFilters = get(criticalityFiltersAtom);

  if (data?.postures_by_criticality) {
    return getPosturesByCriticalityChartData(
      data.postures_by_criticality,
      policy,
      criticalityFilters,
    );
  }
  return undefined;
});

export const topFailingPosturesDataAtom = atom((get) => {
  const data = get(dashboardDataAtom);

  const criticalityFilters = get(criticalityFiltersAtom);
  const policy = get(policyIdFilterAtom);
  if (
    data?.top_failing_postures &&
    Object.keys(data.top_failing_postures).length > 0
  ) {
    return getTopFailingPostureGroupsData(
      data.top_failing_postures,
      policy,
      criticalityFilters,
    );
  }
  return undefined;
});

export const topFailingTechnologiesDataAtom = atom((get) => {
  const data = get(dashboardDataAtom);
  const criticalityFilters = get(criticalityFiltersAtom);
  const policy = get(policyIdFilterAtom);

  if (
    data?.top_failing_technologies &&
    Object.keys(data.top_failing_technologies).length > 0
  ) {
    return getTopFailingTechnologyGroupsData(
      data?.top_failing_technologies,
      policy,
      criticalityFilters,
    );
  }
  return undefined;
});

export const topFailingCategoriesDataAtom = atom((get) => {
  const data = get(dashboardDataAtom);
  const criticalityFilters = get(criticalityFiltersAtom);
  const policy = get(policyIdFilterAtom);

  if (
    data?.top_failing_categories &&
    Object.keys(data.top_failing_categories).length > 0
  ) {
    return getTopFailingCategoryGroupsData(
      data?.top_failing_categories,
      policy,
      criticalityFilters,
    );
  }
  return undefined;
});

/**
 * Get the top failing posture groups data based on criticality filters.
 *
 * @param data - The source data containing posture groups.
 * @param criticalityFilters - The selected criticality filters.
 * @returns An array of GroupingDataItem containing the top failing posture groups.
 */
function getTopFailingPostureGroupsData(
  data,
  policy,
  criticalityFilters: string[],
) {
  const result: GroupingDataItem[] = [];

  // Get the selected severity filters
  const selectedSeverityFilters =
    getSelectedSeverityFilters(criticalityFilters);

  // Determine the policy data to process
  const policyData = policy === 'all' ? data['all'] : data[policy] || [];

  selectedSeverityFilters.forEach((severity) => {
    const severityDataArray = policyData[severity] || [];

    severityDataArray.forEach((item) => {
      // Find or create the item in the result array
      const existingItem = result.find((x) => x.id === item.cid);
      if (existingItem) {
        existingItem.failed_count += item.failed_count;
        existingItem.passed_count += item.passed_count;
      } else {
        result.push({ ...item, id: item.cid, criticality: severity });
      }
    });
  });

  //  sort by failed counts
  result.sort((a, b) => {
    return b.failed_count - a.failed_count;
  });

  return result;
}

/**
 * Get the top failing technology groups data based on criticality filters and policy.
 *
 * @param data - The source data containing technology groups.
 * @param policy - The selected policy ('all' or specific policy ID).
 * @param criticalityFilters - The selected criticality filters.
 * @returns An array of GroupingDataItem containing the top failing technology groups.
 */
function getTopFailingTechnologyGroupsData(
  data,
  policy: PolicyValueType,
  criticalityFilters: string[],
) {
  const result: GroupingDataItem[] = [];

  // Get the selected severity filters
  const selectedSeverityFilters =
    getSelectedSeverityFilters(criticalityFilters);

  // Determine the policy data to process
  const policyData = policy === 'all' ? data['all'] : data[policy] || [];

  selectedSeverityFilters.forEach((severity) => {
    const severityDataArray = policyData[severity] || [];

    severityDataArray.forEach((item) => {
      // Check if the item already exists in the result array
      const existingItem = result.find((x) => x.id === item.technology_id);
      if (existingItem) {
        // If exists, update the counts
        existingItem.failed_count += item.failed_count;
        existingItem.passed_count += item.passed_count;
      } else {
        // Otherwise, insert a copy of the item
        result.push({ ...item, id: item.technology_id, criticality: severity });
      }
    });
  });

  // Sort by failed counts
  result.sort((a, b) => b.failed_count - a.failed_count);

  return result;
}

function getPosturesByCriticalityChartData(
  sourceData,
  policy: PolicyValueType,
  criticalityFilters: string[],
) {
  /**
   * Sample sourceData:
   * {
   *   "labels": ["Urgent", "Critical", "Serious", "Medium", "Minimal"],
   *   "policies": {
   *     "1": {
   *       "Passed": [21, 94, 63, 51, 3],
   *       "Failed": [24, 245, 327, 135, 3]
   *     }
   *   }
   * }
   */
  // if policy is 'all' then merge the data for all policies
  const policies_data = sourceData['policies'];
  const labels = sourceData['labels'];
  const data: {
    labels: string[];
    datasets: number[][];
    passedCount: number;
    failedCount: number;
  } = {
    labels: labels,
    datasets: [],
    passedCount: 0,
    failedCount: 0,
  };

  // otherwise return the data for the selected policy
  if (policy in policies_data) {
    const policyData = policies_data[policy];
    data.datasets = [policyData['Passed'], policyData['Failed']];
  }

  // calculate the total passed and failed counts
  // consider the criticality filters while calculating the counts
  const selectedSeverityFilters =
    getSelectedSeverityFilters(criticalityFilters);
  data.passedCount = data.datasets[0]?.reduce((a, b, i) => {
    return selectedSeverityFilters.includes(labels[i]) ? a + b : a;
  }, 0);
  data.failedCount = data.datasets[1]?.reduce((a, b, i) => {
    return selectedSeverityFilters.includes(labels[i]) ? a + b : a;
  }, 0);

  // console.error('data', data);

  return data;
}

/**
 * Simplified historyChartDataAtom
 * -------------------------------
 * The historyChartDataAtom is a complex atom that fetches and processes historical data for the dashboard.
 *
 * The data structure for the historical data in the dashboardDataAtom is well-defined. It includes:
 *
 * - Weeks and Years Arrays: These arrays map one-to-one, where each week corresponds to a specific year.
 * - Policies Node: This contains child nodes for each policy ID and an "all" node for aggregated data.
 * - Criticality Levels: Each policy node contains nodes for each criticality level (URGENT, CRITICAL, SERIOUS, MEDIUM, MINIMAL).
 * - Passed and Failed Lists: Each criticality node contains passed and failed lists, with values corresponding to the respective week and year.
 *
 * With this structure, we can proceed to implement the necessary changes in the PCDashboard to handle this data format.
 */
export const historyChartDataAtom = atom((get) => {
  const data = get(dashboardDataAtom);

  const severityFilters = get(criticalityFiltersAtom);
  const timespanFilter = get(timeSpanAtom);
  const intervalFilter = get(intervalAtom);
  const policy = get(policyIdFilterAtom);

  if (data) {
    const historyChartData = getHistoryChartData(
      data.historical_data,
      severityFilters,
      timespanFilter,
      intervalFilter,
      policy,
      'policies',
    );

    return historyChartData;
  }
  return [] as number[][];
});

// Get the last sync date from the dashboard data
export const LastSyncDateAtom = atom((get) => {
  const data: any = get(dashboardDataAtom);

  if (data) {
    return moment(data.historical_data?.last_sync_date).format('lll');
  }
  return undefined;
});

// Additional atoms and helper functions similar to other dashboards
// ... (more atoms would go here)

/*************************/
// HELPER FUNCTIONS
/*************************/

// Get history chart data based on filters
function getHistoryChartData(
  historicalData,
  severityFilters: string[],
  timespanFilter: TimespanFilter,
  intervalFilter: IntervalFilter,
  policy: PolicyValueType,
  propertyName: string,
) {
  const passedDataArray: number[][] = [];
  const failedDataArray: number[][] = [];
  let assetsCountArray: number[] = [];
  const resultArray: number[][] = [];

  if (
    !historicalData ||
    !historicalData[propertyName] ||
    !historicalData[propertyName][policy]
  ) {
    return resultArray;
  }

  const selectedSeverityFilters = getSelectedSeverityFilters(severityFilters);
  selectedSeverityFilters.forEach((severityFilter) => {
    passedDataArray.push(
      historicalData[propertyName][policy]['criticality_counts'][severityFilter]
        ?.Passed,
    );
    failedDataArray.push(
      historicalData[propertyName][policy]['criticality_counts'][severityFilter]
        ?.Failed,
    );
  });

  assetsCountArray = historicalData[propertyName][policy].asset_counts;

  const passedData: number[] = mergeArrays(passedDataArray);
  const failedData: number[] = mergeArrays(failedDataArray);
  const intervalFilterType = IntervalFilterType.SkipRecords;

  resultArray.push(
    applyIntervalFilter(
      applyTimespanFilter(passedData, timespanFilter),
      intervalFilter,
      intervalFilterType,
    ),
  );
  resultArray.push(
    applyIntervalFilter(
      applyTimespanFilter(failedData, timespanFilter),
      intervalFilter,
      intervalFilterType,
    ),
  );

  resultArray.push(
    applyIntervalFilter(
      applyTimespanFilter(assetsCountArray, timespanFilter),
      intervalFilter,
      intervalFilterType,
    ),
  );

  // Calculate percentage of passed counts, up to 2 decimal digits
  const percentagePassedData = passedData.map((passedCount, index) => {
    const total = passedCount + failedData[index];
    return total > 0 ? parseFloat(((passedCount / total) * 100).toFixed(2)) : 0;
  });

  resultArray.push(
    applyIntervalFilter(
      applyTimespanFilter(percentagePassedData, timespanFilter),
      intervalFilter,
      intervalFilterType,
    ),
  );

  return resultArray;
}

// Get selected severity filters
function getSelectedSeverityFilters(severityFilters: string[]) {
  let selectedSeverityFilters = severityFilters;
  if (severityFilters.includes(Severity[Severity.All])) {
    selectedSeverityFilters = [
      Severity[Severity.Urgent],
      Severity[Severity.Critical],
      Severity[Severity.Medium],
      Severity[Severity.Serious],
      Severity[Severity.Minimal],
    ];
  }
  return selectedSeverityFilters;
}

/**
 * Get the top failing category groups data based on criticality filters and policy.
 *
 * @param data - The source data containing category groups.
 * @param policy - The selected policy ('all' or specific policy ID).
 * @param criticalityFilters - The selected criticality filters.
 * @returns An array of GroupingDataItem containing the top failing category groups.
 */
function getTopFailingCategoryGroupsData(
  data,
  policy: PolicyValueType,
  criticalityFilters: string[],
) {
  const result: GroupingDataItem[] = [];

  // Get the selected severity filters
  const selectedSeverityFilters =
    getSelectedSeverityFilters(criticalityFilters);

  // Determine the policy data to process
  const policyData = policy === 'all' ? data['all'] : data[policy] || [];

  selectedSeverityFilters.forEach((severity) => {
    const severityDataArray = policyData[severity] || [];

    severityDataArray.forEach((item) => {
      // Check if the item already exists in the result array
      const existingItem = result.find((x) => x.id === item.category);
      if (existingItem) {
        // If exists, update the counts
        existingItem.failed_count += item.failed_count;
        existingItem.passed_count += item.passed_count;
      } else {
        // Otherwise, insert a copy of the item
        result.push({ ...item, id: item.category, criticality: severity });
      }
    });
  });

  // Sort by failed counts
  result.sort((a, b) => b.failed_count - a.failed_count);

  return result;
}
